import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { isPromoCodeEnabled } from "@circle-react/helpers/communityHelpers";
import {
  isChatSpace,
  isCourseSpace,
  isGridOrMasonryViewImageSpace,
  isMembersSpace,
} from "@circle-react/helpers/spaceHelpers";
import { useIsCoursesV2Enabled } from "@circle-react/hooks/courses/useIsCoursesV2Enabled";

import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { useIsMemberAreaAppShellEnabled } from "@/react/hooks/useIsMemberAreaAppShellEnabled";
import { useSpaceSlugFromUrl } from "@/react/hooks/useSpaceSlugFromUrl";
import { useRouteHelpers } from "@/react/routes/Main/useRouteHelpers";

/*
 * Single source of truth for "should the workspace paint a `bg-primary`
 * gutter at this route". The predicate is shared by:
 *   - `useWorkspaceBackgroundClass` (AppShell-era `WorkspaceBackground`)
 *   - `Layout/StandardLayoutV2` (legacy chrome, FF-off)
 *
 * Returns `true` when any of the following is true:
 *   - current space is a chat space
 *   - current space is an image grid / masonry view in the legacy layout
 *   - current space is a course AND the courses v2 FF is on AND
 *     the current route is not the course curriculum page
 *   - on the /members route AND connect is enabled
 *   - the community has the promo-code override enabled
 *   - (AppShell FF only) current space is a members space AND connect is
 *     enabled — this clause is gated to the AppShell so the legacy FF-off
 *     path stays byte-for-byte identical to develop.
 *
 * Keep this predicate in lockstep across consumers — never inline it.
 */
export const useIsBgPrimarySpace = (): boolean => {
  const { currentCommunity } = usePunditUserContext();
  const { isMemberDirectoryPage } = useRouteHelpers();
  const { isCircleV4 } = useCircleV4();
  const spaceSlug = useSpaceSlugFromUrl();
  const space = useSpacesContext().helpers.findBy({ slug: spaceSlug }) || {};

  const isCoursesV2Enabled = useIsCoursesV2Enabled();
  const isCourseCurriculumPage = useRouteHelpers().isSpaceShowPage;

  const isConnectEnabled = useIsConnectEnabled();
  const isMemberAreaAppShellEnabled = useIsMemberAreaAppShellEnabled();
  const isPromoCodeEnabledForCommunity = isPromoCodeEnabled(currentCommunity);

  return (
    isChatSpace(space) ||
    (!isMemberAreaAppShellEnabled && isGridOrMasonryViewImageSpace(space)) ||
    (isMemberAreaAppShellEnabled &&
      isMembersSpace(space) &&
      isConnectEnabled &&
      !isCircleV4) ||
    isPromoCodeEnabledForCommunity ||
    (Boolean(isMemberDirectoryPage) && isConnectEnabled && !isCircleV4) ||
    (isCourseSpace(space) && isCoursesV2Enabled && !isCourseCurriculumPage)
  );
};
